import {
  Dispatch,
  HTMLAttributes,
  MouseEventHandler,
  SetStateAction,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  CircularProgress,
  IconButton,
  TextField
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { OptionCard } from "../../../common-ui/OptionCard";
import { QueryStatus } from "../../../app/types";
import { AvailableParticipant } from "../../Users/usersAPI";
import { AvailableActivity } from "../../Activities/activitiesAPI";

export const emptyActivity: AvailableActivity = {
  activityId: "",
  activityTitle: "",
  sponsorId: "",
  sponsorName: ""
};

type ActivityCardProps = {
  activity: AvailableActivity;
  selectedActivities: (AvailableActivity | undefined)[];
  props?: HTMLAttributes<HTMLLIElement>;
  selected?: boolean;
  setSelectedActivities: Dispatch<
    SetStateAction<(AvailableActivity | undefined)[]>
  >;
  clearable?: boolean;
  deleteable?: boolean;
  deleteFunction?: () => void;
  disabled?: boolean;
  index: number;
  isMenuItem?: boolean;
  autoCompleteOnClick?: MouseEventHandler<HTMLLIElement> | undefined;
};

const ActivityCard = ({
  activity,
  props,
  selected,
  selectedActivities,
  setSelectedActivities,
  clearable,
  deleteable,
  disabled,
  deleteFunction,
  index,
  isMenuItem,
  autoCompleteOnClick
}: ActivityCardProps) => {
  const clearFunction = () => {
    const clonedActivities = [...selectedActivities];
    clonedActivities[index] = undefined;
    setSelectedActivities(clonedActivities);
  };
  if (activity) {
    const sponsorLogo = activity.sponsorLogoUrl ?? undefined;
    return (
      <OptionCard
        avatarImage={sponsorLogo}
        avatarContent={<ReceiptIcon />}
        passedProps={props}
        title={activity.activityTitle ?? "N/A"}
        selected={selected}
        setOption={setSelectedActivities}
        clearFunction={clearFunction}
        clearable={clearable && !disabled}
        deleteable={deleteable && selectedActivities.length > 1}
        deleteFunction={deleteFunction}
        disabled={disabled}
        isMenuItem={isMenuItem}
        autoCompleteOnClick={autoCompleteOnClick}
      />
    );
  }
  return null;
};

type ActivitySelectProps = {
  selectedParticipant: AvailableParticipant | undefined;
  participantAssessmentRole: string | undefined;
  sharedActivitiesStatus: QueryStatus;
  missingRequiredSponsorSelection?: boolean;
  missingRequiredCategorySelection?: boolean;
  availableActivities: AvailableActivity[];
  selectedActivities: (AvailableActivity | undefined)[];
  activity?: AvailableActivity;
  loading?: boolean;
  setSelectedActivities: Dispatch<
    SetStateAction<(AvailableActivity | undefined)[]>
  >;
  activitiesQuery: string;
  setActivitiesQuery: Dispatch<SetStateAction<string>>;
  disabled?: boolean;
  index: number;
  deleteable?: boolean;
};

const setStillAvailableActivities = (
  availableActivities: AvailableActivity[],
  selectedActivities: (AvailableActivity | undefined)[]
) => {
  const stillAvailableActivities = availableActivities
    .filter(
      (availableActivity) =>
        !selectedActivities.some(
          (selectedActivity) => selectedActivity?.activityId === availableActivity.activityId
        )
    )
    .sort((a, b) => {
      if (!a?.sponsorId || !b?.sponsorId) return 0;

      if (a.sponsorId === b.sponsorId) {
        if (!a.activityTitle || !b.activityTitle) return 0;
        return a?.activityTitle > b?.activityTitle ? 1 : -1;
      }
      if (a?.sponsorName === "SIMPL") {
        return 1;
      }
      if (b?.sponsorName === "SIMPL") {
        return -1;
      }
      return a.sponsorId > b.sponsorId ? 1 : -1;
    });

  let currentSponsorId: string | undefined = undefined;
  const insertSpacesAt: number[] = [];
  stillAvailableActivities.forEach((a, idx) => {
    if (!!currentSponsorId && currentSponsorId != a?.sponsorId) {
      insertSpacesAt.push(idx);
    }
    currentSponsorId = a?.sponsorId;
  });

  insertSpacesAt.forEach((num, idx) => {
    stillAvailableActivities.splice(num + idx, 0, {
      activityId: "space",
      activityTitle: "",
      sponsorId: "",
      sponsorName: ""
    });
  });

  return stillAvailableActivities;
};

export const ActivitySelect = ({
  selectedParticipant,
  participantAssessmentRole,
  sharedActivitiesStatus,
  missingRequiredSponsorSelection,
  missingRequiredCategorySelection,
  availableActivities,
  selectedActivities,
  activity,
  setSelectedActivities,
  activitiesQuery,
  setActivitiesQuery,
  loading,
  disabled,
  deleteable,
  index
}: ActivitySelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const stillAvailableActivities = setStillAvailableActivities(
    availableActivities,
    selectedActivities
  );

  const deleteFunction = () => {
    const clonedActivities = [...selectedActivities];
    clonedActivities.splice(index, 1);
    setSelectedActivities(clonedActivities);
  };

  if (activity) {
    return (
      <ActivityCard
        activity={activity}
        selectedActivities={selectedActivities}
        setSelectedActivities={setSelectedActivities}
        selected
        clearable
        deleteable={deleteable}
        deleteFunction={deleteFunction}
        disabled={disabled}
        index={index}
      />
    );
  }
  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      inputValue={activitiesQuery}
      disabled={disabled}
      disableCloseOnSelect
      filterOptions={(x) => x}
      loading={loading}
      options={stillAvailableActivities}
      id="activity-select-input"
      getOptionLabel={(option) =>
        typeof option === "string" ? "N/A" : option.activityTitle ?? "N/A"
      }
      renderOption={(props, option) => {
        if (!option.activityTitle) {
          return <li {...props} style={{ height: 50, listStyle: "none" }} />;
        } else {
          return (
            <ActivityCard
              key={option.activityId}
              activity={option}
              props={props}
              selectedActivities={selectedActivities}
              setSelectedActivities={setSelectedActivities}
              index={index}
              isMenuItem
              autoCompleteOnClick={props.onClick}
            />
          );
        }
      }}
      renderInput={(params) => {
        let label = "";
        if (sharedActivitiesStatus === "loading") {
          label = "Fetching available activities...";
        } else if (!selectedParticipant) {
          label = `Search Activity (Select a ${participantAssessmentRole})`;
        } else if (missingRequiredSponsorSelection) {
          label = "Search Activity (Select an Activity Type)";
        } else if (missingRequiredCategorySelection) {
          label = "Search Activity (Select a Category)";
        } else {
          label = "Search Activity";
        }
        return (
          <TextField
            {...params}
            label={t(label)}
            InputProps={{
              ...params.InputProps,
              style: { paddingRight: "9px" },
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {selectedActivities.length > 1 && (
                    <IconButton
                      aria-label="delete"
                      onClick={() => {
                        if (deleteFunction) {
                          deleteFunction();
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={() => {
                      if (!disabled) {
                        setOpen(!open);
                      }
                    }}
                  >
                    {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                  </IconButton>
                </>
              )
            }}
          />
        );
      }}
      onInputChange={(event, value, reason) => {
        setActivitiesQuery(value);
      }}
      onChange={(event, value, reason) => {
        if (reason === "selectOption" && value) {
          const clonedActivities = [...selectedActivities];
          clonedActivities[index] = value;
          setSelectedActivities(clonedActivities);
        }
      }}
    />
  );
};
